import type { Level, Surface } from '../core/hp/types';
import type { MaterialEntry } from './materials';
import { resolveTexture } from '../render/textures';
import type { MaterialSlotInfo } from '../render/three/MaterialTransformPreview';
import { t } from '../i18n/I18n';

export interface MaterialChoice { id: string; category: string; caption: string; surface: Surface; previewUrl?: string; thumbnailUrl?: string; textureName?: string; hasTexture?: boolean }
export type MaterialSlot = MaterialSlotInfo;
export interface MaterialOffer { choices: MaterialChoice[]; categories: string[]; category: string }
export interface MaterialContextInput { library: MaterialEntry[]; level: Level; surface: 'floor'|'wall'|'item'; current?: Surface; currentSlot?: MaterialSlot; hints?: string[]; offered?: MaterialOffer }

/** Lite MaterialPickerSheet.sameTexture; model-owned names remain owner-scoped. */
function textureName(value: string | undefined) { return value?.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, '').replace(/^mat_/i, '').toLowerCase(); }
export function sameFinish(a: Surface, b: Surface) {
  if (a.textureKind === 'custom' || b.textureKind === 'custom') return a.textureKind === b.textureKind && !!a.customTexture && a.customTexture === b.customTexture;
  if (a.textureKind === 'modelOwn' && b.textureKind === 'modelOwn' && a.textureOwner !== b.textureOwner) return false;
  if (a.texture || b.texture) return !!a.texture && textureName(a.texture) === textureName(b.texture);
  return !!a.color && !!b.color && a.color.every((channel, i) => Math.abs(channel - b.color![i]) < .02);
}
export function matchesMaterialChoice(choice: MaterialChoice, current?: Surface, slot?: MaterialSlot) {
  return !!current && (sameFinish(choice.surface, current) ||
    (!current.texture && !current.customTexture && !current.color && !!slot?.hasTexture && !!slot.previewUrl && choice.previewUrl === slot.previewUrl));
}
export function rememberMaterial(recents: MaterialChoice[], choice: MaterialChoice) {
  const previous = recents.find(value => sameFinish(value.surface, choice.surface) ||
    (!choice.surface.texture && !choice.surface.customTexture && !choice.surface.color && !!choice.previewUrl && value.previewUrl === choice.previewUrl));
  const recent = { ...choice, id: previous?.id ?? `recent:${JSON.stringify([choice.surface, choice.previewUrl])}` };
  return [recent, ...recents.filter(value => value.id !== recent.id)].slice(0, 24);
}
export function materialCategory(surface: 'floor'|'wall'|'item', material: Surface, hints: string[] = []) {
  if (material.textureKind === 'custom') return 'Recent';
  if (!material.texture && !hints.some(Boolean)) return 'colors';
  const text = [material.texture, material.caption, ...hints].join(' ').toLowerCase();
  const has = (pattern: RegExp) => pattern.test(text);
  // i18n-ignore-start: words matched in texture names, captions and slot names, not interface text
  if (surface === 'item') {
    if (has(/screen|display|monitor|television|\btv\b|экран|телевиз|picture|painting|poster|photo|background|window.?view|artwork|scenery|картина|фото|пейзаж/)) return 'ArtFinishes';
    if (has(/leather|couro|skin|кож/)) return 'Leather';
    if (has(/abric|cloth|textile|linen|cotton|velvet|wool|tecido|canvas|felt|knit|plush|ткан/)) return 'Fabric';
    if (has(/metal|steel|chrome|alumin|brass|bronze|gold|silver|copper|inox|металл/)) return 'Metal';
    if (has(/marble|мрамор/)) return 'Marble';
    if (has(/wood|oak|dub|beech|birch|walnut|maple|teak|plank|parquet|timber|veneer|mdf|laminat|straw|rattan|wicker|дерев|дуб/)) return 'Wood';
    if (has(/sofa|chair|bed|диван|кресло/)) return 'Fabric';
    if (has(/lamp|light|appliance/)) return 'Metal';
    return 'ArtFinishes';
  }
  if (has(/marble|мрамор/)) return 'Marble';
  if (has(/granite|гранит/)) return 'Granite';
  if (has(/tile|ceramic|плитк/)) return 'Tile';
  if (surface === 'floor') return has(/carpet|rug|ков/)? 'Carpet' : 'Wood';
  if (has(/brick|кирпич/)) return 'Bricks';
  if (has(/panel|wood|дерев/)) return 'Panels';
  if (has(/plaster|paint|stucco|штукатур/)) return 'GeneralFinishes';
  // i18n-ignore-end
  return 'Wallpapers';
}

export function materialContext({ library, level, surface, current, currentSlot, hints = [], offered }: MaterialContextInput) {
  if (offered) {
    const selectedId = offered.choices.find(choice => matchesMaterialChoice(choice, current, currentSlot))?.id;
    return { ...offered, selectedId };
  }
  const list = library.filter(m => m.surface === surface || (surface === 'item' && ['Wood', 'Marble', 'Tile'].includes(m.collection)));
  const catalogByName = new Map<string | undefined, MaterialEntry>();
  for (const entry of list) { const name = textureName(entry.texture); if (!catalogByName.has(name)) catalogByName.set(name, entry); }
  const offeredTextureNames = new Set<string | undefined>();
  const choices: MaterialChoice[] = [];
  const add = (material: Surface | undefined, extraHints: string[] = [], slot?: MaterialSlot, id?: string) => {
    if (!material || (!material.texture && !material.customTexture && !material.color && !slot?.hasTexture)) return;
    const existing = choices.find(choice => sameFinish(choice.surface, material));
    if (existing) return existing.id;
    const match = material.textureKind !== 'custom' && material.texture ? catalogByName.get(textureName(material.texture)) : undefined;
    const category = match?.collection ?? (!material.texture && !material.customTexture && !slot?.hasTexture && material.color ? 'colors' : materialCategory(surface, material, [...extraHints, slot?.textureName ?? '', slot?.label ?? '']));
    const choice: MaterialChoice = {
      id: id ?? `project-${choices.length}`, category,
      caption: material.caption || slot?.label || match?.caption || (category === 'Recent' ? t('#MaterialContextFromProject') : t('#MaterialContextProjectMaterial')),
      surface: material, previewUrl: slot?.previewUrl ?? resolveTexture(material)?.url, thumbnailUrl: match?.thumb,
      textureName: slot?.textureName, hasTexture: slot?.hasTexture ?? !!(material.texture || material.customTexture),
    };
    choices.push(choice);
    if (material.textureKind !== 'custom' && material.texture) offeredTextureNames.add(textureName(material.texture));
    return choice.id;
  };
  // The current finish comes first so aliases never replace its authored tint,
  // model owner, UV transform or user-upload URL with a catalog default.
  const selectedId = add(current, hints, currentSlot, 'current');
  if (surface === 'item') {
    const addModule = (modules: Level['items'][number]['modules']) => modules?.forEach(module => { Object.entries(module.materials ?? {}).forEach(([key, value]) => add(value, [key, module.name])); addModule(module.modules); });
    for (const item of level.items) { Object.entries(item.materials ?? {}).forEach(([key, value]) => add(value, [key, item.caption ?? ''])); addModule(item.modules); }
  } else if (surface === 'wall') {
    for (const wall of level.walls) { add(wall.frontSurface); add(wall.backSurface); }
  } else for (const room of level.rooms) { add(room.floorSurface); add(room.ceilingSurface); }
  for (const entry of list) {
    const name = textureName(entry.texture);
    if (entry.texture && offeredTextureNames.has(name)) continue;
    const material: Surface = { texture: entry.texture, textureKind: 'catalog', tileMeters: entry.tile, shininess: 1 - entry.roughness, caption: entry.caption };
    choices.push({ id: `catalog:${entry.texture}`, category: entry.collection, caption: entry.caption, surface: material, previewUrl: entry.url, thumbnailUrl: entry.thumb, hasTexture: true });
    if (entry.texture) offeredTextureNames.add(name);
  }
  const categories = [...new Set(['Recent', ...list.map(m => m.collection), ...choices.map(m => m.category), 'colors'])];
  const category = choices.find(choice => choice.id === selectedId)?.category ?? categories.find(value => value !== 'Recent') ?? 'colors';
  return { choices, categories, category, selectedId };
}
